import { Grid } from '@mui/material';
import Skeleton from 'Theme/components/Skeleton';
import useTranslate from './useTranslate';
import { FormattedDate } from 'react-intl';
import { UserNameCell } from 'Components/Shared/UserNameCell';
import { TableCollectionColumn } from 'Components/Shared/Table';
import UserStatusChip from 'Components/Shared/Chips/UserStatusChip/UserStatusChip';
import { OrganizationPersonOrder } from 'Apollo';
import ContextualInfo from 'Components/ContextualInfo';

const MAX_ACCOUNT_NAME_CHARS = 3;
const MAX_ACCOUNTS_DISPLAYED = 4;

function AccountNameList({ names }: Readonly<{ names: string[] }>): React.ReactNode {
  if (names.length < MAX_ACCOUNTS_DISPLAYED) return names.join(', ');
  const displayed = names.map(n => n.slice(0, MAX_ACCOUNT_NAME_CHARS)).join(', ');
  return (
    <Grid container alignItems="center" wrap="nowrap">
      {displayed}
      <ContextualInfo
        content={names.join(', ')}
        style={{ marginLeft: '8px' }}
      />
    </Grid>
  );
}

export const useColumnsOrganizationUsers = (): TableCollectionColumn<{
  node: IOrganizationPerson;
}>[] => {
  const ts = useTranslate();

  return [
    {
      key: 'user',
      orderBy: {
        asc: OrganizationPersonOrder.NameAsc,
        desc: OrganizationPersonOrder.NameDesc
      },
      renderHead: () => ts('page.users.index.users'),
      renderSkeleton: () => (
        <Grid container={true} alignItems="center">
          <Skeleton variant="circle" width={24} height={24} />
          <Skeleton
            variant="rectangular"
            width="20%"
            style={{ marginLeft: '16px' }}
          />
        </Grid>
      ),
      renderCell: ({
        row: { node: person }
      }: {
        row: { node: IOrganizationPerson };
      }) => <UserNameCell person={person} />
    },
    {
      key: 'creation',
      renderHead: () => ts('page.users.index.creationDate'),
      renderSkeleton: () => <Skeleton variant="rectangular" width="80%" />,
      renderCell: ({
        row: { node: person }
      }: {
        row: { node: IOrganizationPerson };
      }) => (
        <FormattedDate
          value={person.creationDate}
          year="numeric"
          month="2-digit"
          day="2-digit"
          hour="2-digit"
          minute="2-digit"
        />
      )
    },
    {
      key: 'status',
      renderHead: () => ts('page.users.index.allStatus'),
      renderSkeleton: () => <Skeleton variant="rectangular" width="10%" />,
      renderCell: ({
        row: { node: person }
      }: {
        row: { node: IOrganizationPerson };
      }) => <UserStatusChip person={person as unknown as IPerson} />
    },
    {
      key: 'email',
      renderHead: () => ts('page.users.index.email'),
      renderSkeleton: () => <Skeleton variant="rectangular" width="80%" />,
      renderCell: ({
        row: { node: person }
      }: {
        row: { node: IOrganizationPerson };
      }) => person.email
    },
    {
      key: 'directories',
      renderHead: () => ts('profile.about.authenticationMode'),
      renderSkeleton: () => <Skeleton variant="rectangular" width="80%" />,
      renderCell: ({
        row: { node: person }
      }: {
        row: { node: IOrganizationPerson };
      }) =>
        person.identityDirectories
          .map(d => d === 'Cegid Passeport' ? ts('profile.about.localAccountConnectionMode') : d)
          .join(', ')
    },
    {
      key: 'domain',
      orderBy: {
        asc: OrganizationPersonOrder.DomainAsc,
        desc: OrganizationPersonOrder.DomainDesc
      },
      renderHead: () => ts('page.organizationProfile.menu.domains'),
      renderSkeleton: () => <Skeleton variant="rectangular" width="60%" />,
      renderCell: ({
        row: { node: person }
      }: {
        row: { node: IOrganizationPerson };
      }) => person.domain
    },
    {
      key: 'accounts',
      renderHead: () => ts('common.terminology.accounts'),
      renderSkeleton: () => <Skeleton variant="rectangular" width="80%" />,
      renderCell: ({
        row: { node: person }
      }: {
        row: { node: IOrganizationPerson };
      }) => AccountNameList({ names: (person.subscribedAccounts ?? []).map(a => a.name ?? '') })
    },
    {
      key: 'administratedAccounts',
      renderHead: () => ts('page.administrators.title'),
      renderSkeleton: () => <Skeleton variant="rectangular" width="80%" />,
      renderCell: ({
        row: { node: person }
      }: {
        row: { node: IOrganizationPerson };
      }) =>
        (person.administratedAccounts ?? []).length === 0
          ? ts('common.action.no')
          : AccountNameList({ names: (person.administratedAccounts ?? []).map(a => a.name ?? '') })
    }
  ];
};
